import {
  cardPointMode,
  createCardDraft,
  cardDraftOperation,
  cardDraftFingerprint
} from '../lib/racquetCardDraft'
import { useState, useRef, useEffect } from 'react'
import RacquetModal from './RacquetModal'

import { pointLabels } from '../../../shared/racquetScoreDomain.mjs'
import { racquetErrorKey } from '../lib/racquetApi'
export function RacquetScoreBreakdown({ state: s, t, teamName }) {
  const racket = ['tennis', 'padel'].includes(s.rules.sport)
  return (
    <div className="rp-breakdown">
      <div className="rp-card-row">
        <span />
        {s.teams.map((team) => (
          <strong key={team.id}>{teamName(team)}</strong>
        ))}
      </div>
      {s.segments.map((seg, i) => (
        <div className="rp-card-row" key={i}>
          <span>
            {t(racket ? 'set' : 'game')} {i + 1}
          </span>
          {seg.score.map((n, j) => (
            <strong key={j}>{n}</strong>
          ))}
        </div>
      ))}
      {((s.games.some(Boolean) && racket) || s.points.some(Boolean)) && (
        <div className="rp-card-row">
          <span>{t('unfinishedSegment')}</span>
          {(racket ? s.games : s.points).map((n, j) => (
            <strong key={j}>{n}</strong>
          ))}
        </div>
      )}
      {racket && s.points.some(Boolean) && (
        <div className="rp-card-row">
          <span>{t('points')}</span>
          {pointLabels(s).map((n, j) => (
            <strong key={j}>{n === 'AD' ? t('advantage') : n}</strong>
          ))}
        </div>
      )}
    </div>
  )
}
export default function RacquetDirectCard({
  match: m,
  t,
  teamName,
  onPreview,
  viewId,
  disabled,
  editor
}) {
  const s = m.state,
    racket = ['tennis', 'padel'].includes(s.rules.sport)
  const [local, setLocal] = useState(() => createCardDraft(m)),
    [localError, setLocalError] = useState('')
  const [options, setOptions] = useState(false)
  const root = useRef(null),
    sheet = useRef(null),
    feedback = useRef(null)
  useEffect(() => {
    if (!options) return
    const previous = document.activeElement
    sheet.current?.focus()
    const key = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        setOptions(false)
      }
      if (e.key === 'Tab') {
        const list = [
          ...sheet.current.querySelectorAll('button,input,select')
        ].filter((n) => !n.disabled)
        if (e.shiftKey && document.activeElement === list[0]) {
          e.preventDefault()
          list.at(-1)?.focus()
        } else if (!e.shiftKey && document.activeElement === list.at(-1)) {
          e.preventDefault()
          list[0]?.focus()
        }
      }
    }
    sheet.current?.addEventListener('keydown', key)
    return () => {
      sheet.current?.removeEventListener('keydown', key)
      previous?.focus()
    }
  }, [options])
  const [invalidRow, setInvalidRow] = useState(null)
  const focusError = (rowIndex, pointsIssue) =>
    requestAnimationFrame(() => {
      const target = pointsIssue
        ? sheet.current
        : Number.isInteger(rowIndex)
          ? root.current
              ?.querySelectorAll('.rp-entry-row')
              [rowIndex]?.querySelector('input')
          : feedback.current
      target?.focus()
      target?.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
    })
  const values = editor?.values || local,
    { rows, points, pointMode } = values
  const change = (update) =>
    editor
      ? editor.change(update)
      : setLocal((old) => ({
          ...old,
          ...(typeof update === 'function' ? update(old) : update)
        }))
  const error = editor?.error || localError
  const setError = editor?.setError || setLocalError
  const frozen =
    disabled ||
    Boolean(values.pendingEventId) ||
    editor?.error === 'draft_unreadable'
  const update = (i, patch) =>
    change((old) => ({
      rows: old.rows.map((row, j) => (i === j ? { ...row, ...patch } : row))
    }))
  const review = () => {
    try {
      const { operation, after } = cardDraftOperation(values, m, {
        reason: t('directEntryReason'),
        finish: true,
        review: true
      })
      setError('')
      setInvalidRow(null)
      onPreview({
        operation,
        before: s,
        after,
        revision: m.revision,
        viewId,
        cardDraft: true,
        draftFingerprint: cardDraftFingerprint(values),
        replacement: m.events.length > 0
      })
    } catch (e) {
      setError(e.code || e.message)
      const pointsIssue = [
        'completed_segment_has_points',
        'point_mode_changed',
        'point_combination_invalid',
        'invalid_point_score'
      ].includes(e.code)
      setInvalidRow(e.rowIndex ?? null)
      if (pointsIssue) setOptions(true)
      focusError(e.rowIndex, pointsIssue)
    }
  }
  const changed =
    values.baseRevision !== m.revision ||
    (values.baseEventId ?? null) !== (m.events.at(-1)?.id || null)
  const modeChanged = racket && cardPointMode(rows) !== pointMode
  return (
    <section
      ref={root}

      className="rp-direct"
      role="region"
      aria-label={t('scorecard')}
      tabIndex={0}
    >
      <div className="rp-card-row">
        <span>{t(racket ? 'sets' : 'games')}</span>
        {s.teams.map((team) => (
          <strong key={team.id}>{teamName(team)}</strong>
        ))}
      </div>
      {rows.map((row, i) => (
        <div className="rp-entry-row" key={i}>
          <div className="rp-card-row">
            <span>
              {t(racket ? 'set' : 'game')} {i + 1}
            </span>
            {row.score.map((n, j) => (
              <input
                key={j}
                aria-label={`${t(racket ? 'set' : 'game')} ${i + 1} ${teamName(s.teams[j])}`}
                aria-invalid={invalidRow === i && Boolean(error)}
                inputMode="numeric"
                pattern="[0-9]*"
                value={n}
                disabled={frozen}
                onChange={(e) =>
                  update(i, {
                    score: row.score.map((v, k) =>
                      j === k ? e.target.value : v
                    )
                  })
                }
              />
            ))}
          </div>
          <label className="rp-check">
            <input
              type="checkbox"
              disabled={frozen || i < rows.length - 1}
              checked={row.completed}
              onChange={(e) => update(i, { completed: e.target.checked })}
            />
            {t(racket ? 'completedSet' : 'completedGame')}
          </label>
          {i === rows.length - 1 && rows.length > 1 && (
            <button
              className="rp-remove-row"
              aria-label={`${t('remove')} ${t(racket ? 'set' : 'game')} ${i + 1}`}
              disabled={frozen}
              onClick={() => change((old) => ({ rows: old.rows.slice(0, -1) }))}
            >
              {t('remove')}
            </button>
          )}
        </div>
      ))}
      <button
        className="rp-add-row"
        disabled={frozen || !rows.at(-1)?.completed}
        onClick={() =>
          change((old) => ({
            rows: [...old.rows, { score: ['0', '0'], completed: false }]
          }))
        }
      >
        {t(racket ? 'addSet' : 'addGame')}
      </button>
      <button className="rp-more-options" onClick={() => setOptions(true)}>
        {t('moreOptions')}
      </button>
      {options && (
        <RacquetModal>
          <section
            ref={sheet}
            tabIndex={-1}
            className="rp-dialog"
            role="dialog"
            aria-modal="true"
            aria-label={t('moreOptions')}
          >
            <h2>{t('moreOptions')}</h2>
            {error && <p role="alert">{t(racquetErrorKey(error))}</p>}
            {racket && (
              <>
                <h3>{t('currentPointsOptional')}</h3>
                <p>{t('pointsForUnfinished')}</p>
                {rows.at(-1)?.completed && points.some((n) => n !== '0') && (
                  <div role="alert">
                    <p>{t('error_completed_segment_has_points')}</p>
                    <button
                      onClick={() =>
                        change({
                          points: ['0', '0'],
                          pointMode: cardPointMode(rows)
                        })
                      }
                    >
                      {t('clearStalePoints')}
                    </button>
                    <button
                      onClick={() =>
                        change((old) => ({
                          rows: [
                            ...old.rows,
                            { score: ['0', '0'], completed: false }
                          ]
                        }))
                      }
                    >
                      {t('retainUnfinishedPoints')}
                    </button>
                  </div>
                )}
                {modeChanged && points.some((n) => n !== '0') && (
                  <p role="alert">
                    {t('pointModeChanged')}{' '}
                    <button
                      disabled={frozen}
                      onClick={() =>
                        change({
                          pointMode: cardPointMode(rows),
                          points: ['0', '0']
                        })
                      }
                    >
                      {t(
                        cardPointMode(rows) === 'tieBreak'
                          ? 'useTieBreakPoints'
                          : 'useOrdinaryPoints'
                      )}
                    </button>
                  </p>
                )}
                <div className="rp-card-row">
                  <span>{t('points')}</span>
                  {points.map((n, j) =>
                    pointMode === 'tieBreak' ? (
                      <input
                        key={j}
                        aria-label={`${t('tieBreak')} ${teamName(s.teams[j])}`}
                        disabled={frozen || Boolean(rows.at(-1)?.completed)}
                        inputMode="numeric"
                        value={n}
                        onChange={(e) =>
                          change((old) => ({
                            points: old.points.map((v, k) =>
                              j === k ? e.target.value : v
                            )
                          }))
                        }
                      />
                    ) : (
                      <select
                        key={j}
                        aria-label={`${t('points')} ${teamName(s.teams[j])}`}
                        disabled={frozen || Boolean(rows.at(-1)?.completed)}
                        value={n}
                        onChange={(e) =>
                          change((old) => ({
                            points: old.points.map((v, k) =>
                              j === k ? e.target.value : v
                            )
                          }))
                        }
                      >
                        {['0', '15', '30', '40', 'AD'].map((value) => (
                          <option
                            key={value}
                            value={value}
                            disabled={value === 'AD' && points[1 - j] !== '40'}
                          >
                            {value === '0'
                              ? t('loveZero')
                              : value === 'AD'
                                ? t('advantage')
                                : value}
                          </option>
                        ))}
                      </select>
                    )
                  )}
                </div>
              </>
            )}
            {editor?.dirty && !values.pendingEventId && (
              <button
                disabled={frozen}
                onClick={() => {
                  editor.discard()
                  setOptions(false)
                }}
              >
                {t('discardChanges')}
              </button>
            )}
            <button onClick={() => setOptions(false)}>
              {t('backToScores')}
            </button>
          </section>
        </RacquetModal>
      )}

      {changed && (
        <section className="rp-notice">
          <p>{t('draftRevisionChanged')}</p>
          <RacquetScoreBreakdown state={s} t={t} teamName={teamName} />
          <label className="rp-check">
            <input
              type="checkbox"
              disabled={frozen}
              data-reconcile-consent
              checked={
                values.reconcileRevision === m.revision &&
                (values.reconcileEventId ?? null) ===
                  (m.events.at(-1)?.id || null)
              }
              onChange={(e) =>
                change({
                  reconcileRevision: e.target.checked ? m.revision : null,
                  reconcileEventId: e.target.checked
                    ? m.events.at(-1)?.id || null
                    : null
                })
              }
            />
            {t('reconcileDraft')}
          </label>
        </section>
      )}
      {editor?.storageError && (
        <p role="alert">
          {t('draftStorageError')}{' '}
          <button onClick={editor.retryStorage}>{t('retry')}</button>
        </p>
      )}
      {values.pendingEventId && <p role="status">{t('draftPending')}</p>}
      {error && !options && (
        <p ref={feedback} tabIndex={-1} role="alert">
          {t(racquetErrorKey(error))}
        </p>
      )}

      <button className="rp-primary" disabled={frozen} onClick={review}>
        {t('reviewSave')}
      </button>
    </section>
  )
}
export function RacquetServiceSetup({
  state: s,
  t,
  names,
  onSubmit,
  disabled
}) {
  const [server, setServer] = useState(''),
    [reverse, setReverse] = useState(''),
    [box, setBox] = useState(''),
    [number, setNumber] = useState(''),
    [right, setRight] = useState(s.teams.map(() => ''))
  const doubles = s.teams[0].players.length === 2,
    pickle = s.rules.sport === 'pickleball',
    openingPickle = pickle && !s.summaryOnly && !s.server
  const submit = () => {
    const first = s.teams.find((team) => team.players.includes(server)),
      second = s.teams.find((team) => team !== first)
    const a = [server, ...first.players.filter((p) => p !== server)],
      b = reverse === 'true' ? [...second.players].reverse() : second.players
    const positions = s.teams.map((team, i) =>
      team.players.length === 1
        ? [team.players[0], null]
        : [
            openingPickle && team.players.includes(server) ? server : right[i],
            team.players.find(
              (p) =>
                p !==
                (openingPickle && team.players.includes(server)
                  ? server
                  : right[i])
            )
          ]
    )
    onSubmit({
      type: 'configure_service',
      server,
      serviceOrder: a.flatMap((p, i) => [p, b[i]]),
      serviceBox: pickle
        ? doubles
          ? openingPickle || right[s.teams.indexOf(first)] === server
            ? 'right'
            : 'left'
          : s.points[s.teams.indexOf(first)] % 2 === 0
            ? 'right'
            : 'left'
        : s.rules.sport === 'squash'
          ? box
          : 'right',
      ...(pickle
        ? {
            serverNumber: doubles ? (openingPickle ? 2 : number) : 1,
            positions
          }
        : {})
    })
  }
  return (
    <section className="rp-review">
      <label>
        {t(
          s.tieBreak
            ? 'tieBreakFirstServer'
            : s.summaryOnly || s.server
              ? 'server'
              : 'firstServer'
        )}
        <select
          value={server}
          onChange={(e) => {
            setServer(e.target.value)
            setReverse('')
          }}
        >
          <option value="">{t('choose')}</option>
          {s.teams
            .flatMap((team) => team.players)
            .map((p) => (
              <option key={p} value={p}>
                {names.get(p)}
              </option>
            ))}
        </select>
      </label>
      {doubles && !pickle && (
        <label>
          {t('receivingFirst')}
          <select value={reverse} onChange={(e) => setReverse(e.target.value)}>
            <option value="">{t('choose')}</option>
            {(server
              ? s.teams.find((team) => !team.players.includes(server)).players
              : []
            ).map((p, i) => (
              <option key={p} value={String(i === 1)}>
                {names.get(p)}
              </option>
            ))}
          </select>
        </label>
      )}
      {s.rules.sport === 'squash' && (
        <label>
          {t('initialBox')}
          <select value={box} onChange={(e) => setBox(e.target.value)}>
            <option value="">{t('choose')}</option>
            <option value="right">{t('right')}</option>
            <option value="left">{t('left')}</option>
          </select>
        </label>
      )}
      {pickle && doubles && (
        <>
          {!openingPickle && (
            <label>
              {t('serverNumber')}
              <select
                value={number}
                onChange={(e) => setNumber(Number(e.target.value))}
              >
                <option value="">{t('choose')}</option>
                <option value={1}>1</option>
                <option value={2}>2</option>
              </select>
            </label>
          )}
          {s.teams.map(
            (team, i) =>
              (!openingPickle ||
                (server && !team.players.includes(server))) && (
                <label key={team.id}>
                  {t('rightCourt')} {i + 1}
                  <select
                    value={right[i]}
                    onChange={(e) =>
                      setRight((old) =>
                        old.map((v, j) => (i === j ? e.target.value : v))
                      )
                    }
                  >
                    <option value="">{t('choose')}</option>
                    {team.players.map((p) => (
                      <option key={p} value={p}>
                        {names.get(p)}
                      </option>
                    ))}
                  </select>
                </label>
              )
          )}
        </>
      )}
      <button
        disabled={
          disabled ||
          !server ||
          (doubles && !pickle && reverse === '') ||
          (s.rules.sport === 'squash' && !box) ||
          (pickle &&
            doubles &&
            ((!openingPickle && !number) ||
              right.some(
                (p, i) =>
                  !p && (!openingPickle || !s.teams[i].players.includes(server))
              )))
        }
        className="rp-primary"
        onClick={submit}
      >
        {t('startLive')}
      </button>
    </section>
  )
}
